import _ from 'lodash';
import {
  Program,
  WithOptionalId,
  AssignedProgram,
  Member,
  PagedResponse,
  PaginatedListParams,
} from '../../types';
import { apiBaseUrl } from '../apiConsts';
import { buildPaginationQuery, createEmptyPagedResponse, MAX_PAGE_SIZE } from '../pagination';
import { getDefaultError } from '../../utils';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import {
  isValidProgramResponse,
  isValidProgramsResponse,
} from '../apiValidators';
import { updateMemberData, fetchMembersByIds } from './members-controller';

import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { AppError } from '../../error';

class ProgramAssignmentError extends Error {
  error: AppError;
  status?: number;
  durationMs?: number;

  constructor({
    error,
    status,
    durationMs,
  }: {
    error: AppError;
    status?: number;
    durationMs?: number;
  }) {
    super(error.message);
    this.name = 'ProgramAssignmentError';
    this.error = error;
    this.status = status;
    this.durationMs = durationMs;
  }
}

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

export const createNewProgram = async (
  program: WithOptionalId<Program>,
  trainerId: string,
): Promise<FetchResult<Program | null>> => {
  if (!trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Trainer id must be provided'),
    };
  }
  const { createdDate, updatedDate, weeksCount, imageUrl, clientCount, startDate, ...payload } =
    program as WithOptionalId<Program> & { startDate?: string };

  const createResponse = await getDataWithRetry<Program | null>(
    `${apiBaseUrl}programs`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(payload),
    },
    isValidProgramResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }
  const associateProgramResult = await sendWithoutBody(
    `${apiBaseUrl}trainers/${trainerId}/associate/program`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createResponse.data.id]),
    },
  );
  if (!associateProgramResult.success) {
    return associateProgramResult;
  }
  return {
    ...createResponse,
    data: {
      ...createResponse.data,
      trainerId,
    },
  };
};

export const fetchProgramById = (
  programId: string,
): Promise<FetchResult<Program | null>> => {
  if (!programId) {
    return Promise.resolve({
      data: null,
      success: false,
      error: getDefaultError('Program not found'),
    });
  }
  return getDataWithRetry(
    `${apiBaseUrl}programs/${programId}`,
    {},
    isValidProgramResponse,
  );
};

export const updateProgramData = (
  program: Program,
): Promise<FetchResult<Program | null>> => {
  const { createdDate, updatedDate, weeksCount, imageUrl, clientCount, startDate, ...programPayload } =
    program as Program & { startDate?: string };
  
  return getDataWithRetry(
    `${apiBaseUrl}programs`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(programPayload),
    },
    isValidProgramResponse,
  );
};

export const deleteProgramById = (
  programId: string,
): Promise<FetchResult<null>> => {
  return sendWithoutBody(`${apiBaseUrl}programs/${programId}`, {
    method: 'DELETE',
  });
};

export const fetchTrainerPrograms = async (
  trainerId: string,
): Promise<FetchResult<Program[]>> => {
  const response = await getDataWithRetry<PagedResponse<Program>>(
    `${apiBaseUrl}programs/getByTrainerIds?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([trainerId]),
    },
    isValidProgramsResponse,
  );
  return mapPagedResult(response);
};

export const fetchProgramsPageByTrainerIds = (
  trainerIds: string[],
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<Program>>> => {
  if (!trainerIds || !trainerIds.length) {
    return Promise.resolve(
      createSuccessResult(createEmptyPagedResponse<Program>(params)),
    );
  }
  return getDataWithRetry<PagedResponse<Program>>(
    `${apiBaseUrl}programs/getByTrainerIds?${buildPaginationQuery(params)}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(trainerIds),
    },
    isValidProgramsResponse,
  );
};

export const assignProgramToMembers = async (
  memberIds: string[],
  program: AssignedProgram,
): Promise<FetchResult<Member[]>> => {
  if (!memberIds || !memberIds.length) {
    return createSuccessResult<Member[]>([]);
  }
  const membersResponse = await fetchMembersByIds(memberIds);
  if (!membersResponse.success || !membersResponse.data) {
    return {
      success: false,
      data: null,
      error:
        membersResponse.error ||
        getDefaultError('Failed to fetch members for assignment'),
      status: membersResponse.status,
      durationMs: membersResponse.durationMs,
    };
  }
  const membersById = _.keyBy(membersResponse.data, 'id');
  let assignedProgramSnapshot: AssignedProgram = program;

  if (!Array.isArray(program.programItems)) {
    const fullProgramResponse = await fetchProgramById(program.id);
    if (!fullProgramResponse.success || !fullProgramResponse.data) {
      return {
        success: false,
        data: null,
        error:
          fullProgramResponse.error ||
          getDefaultError('Failed to load full program for assignment'),
        status: fullProgramResponse.status,
        durationMs: fullProgramResponse.durationMs,
      };
    }
    assignedProgramSnapshot = {
      ...fullProgramResponse.data,
      startDate: program.startDate,
    };
  }

  try {
    const updatedMembers = await Promise.all(
      memberIds.map(async (memberId) => {
        const member = membersById[memberId];
        if (!member) {
          throw new ProgramAssignmentError({
            error: getDefaultError(`Member ${memberId} not found`),
          });
        }
        const updateResult = await updateMemberData(member, {
          ...member,
          program: assignedProgramSnapshot,
        });
        if (!updateResult.success || !updateResult.data) {
          throw new ProgramAssignmentError({
            error:
              updateResult.error ||
              getDefaultError('Failed to assign program to member'),
            status: updateResult.status,
            durationMs: updateResult.durationMs,
          });
        }
        return updateResult.data;
      }),
    );

    return createSuccessResult<Member[]>(updatedMembers);
  } catch (failure) {
    const typedFailure =
      failure instanceof ProgramAssignmentError ? failure : null;
    return {
      success: false,
      data: null,
      error:
        typedFailure?.error ??
        getDefaultError('Failed to assign program to member'),
      status: typedFailure?.status,
      durationMs: typedFailure?.durationMs,
    };
  }
};
